/**
 * Budget flow Sankey diagram — shows money flowing from budget → campaigns → outcomes.
 * Uses Nivo Sankey chart.
 */

import { ResponsiveSankey } from '@nivo/sankey'
import { useNivoTheme, useChartColors } from './nivoTheme'
import { formatMarketingCents } from '../format'

export interface SankeyNode {
  id: string
  label?: string
  color?: string
}

export interface SankeyLink {
  source: string
  target: string
  value: number
}

export interface BudgetSankeyData {
  nodes: SankeyNode[]
  links: SankeyLink[]
}

interface BudgetSankeyProps {
  data: BudgetSankeyData
  height?: number
  highlights?: string[]
}

const formatCurrency = formatMarketingCents

export function BudgetSankey({
  data,
  height = 400,
  highlights,
}: BudgetSankeyProps) {
  const theme = useNivoTheme()
  const chartColors = useChartColors()

  if (data.nodes.length === 0 || data.links.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No budget flow data available
      </div>
    )
  }

  return (
    <div style={{ height }}>
      <ResponsiveSankey
        data={data}
        theme={theme}
        margin={{ top: 20, right: 160, bottom: 20, left: 20 }}
        align="justify"
        colors={(node: { id: string }) => {
          if (highlights?.includes(node.id)) return '#f59e0b'
          const idx = data.nodes.findIndex((n) => n.id === node.id)
          return data.nodes[idx]?.color || chartColors[idx % chartColors.length]
        }}
        nodeOpacity={1}
        nodeHoverOthersOpacity={0.3}
        nodeThickness={18}
        nodeSpacing={24}
        nodeBorderWidth={0}
        nodeBorderRadius={3}
        linkOpacity={0.4}
        linkHoverOthersOpacity={0.1}
        linkContract={3}
        linkBlendMode="normal"
        enableLinkGradient
        labelPosition="outside"
        labelOrientation="horizontal"
        labelPadding={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1]] }}
        motionConfig="gentle"
        nodeTooltip={({ node }) => (
          <div className="bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border">
            <p className="font-semibold" style={{ color: node.color }}>{node.label}</p>
            <p className="text-muted-foreground">{formatCurrency(node.value)}</p>
          </div>
        )}
        linkTooltip={({ link }) => (
          <div className="bg-popover rounded-lg shadow-lg px-3 py-2 text-sm border border-border">
            <p className="text-muted-foreground">
              {link.source.label} → {link.target.label}
            </p>
            <p className="font-semibold">{formatCurrency(link.value)}</p>
          </div>
        )}
      />
    </div>
  )
}
